/**
 * The one lexer for the Formula language. It never throws: text it cannot read
 * becomes an `invalid` token, so display rewriting and the editor keep working on
 * half-typed Formulas while the parser reports the first problem with its span.
 */

/** Character offsets into the Formula text as typed, `end` exclusive. */
export interface Span {
  start: number;
  end: number;
}

export type FormulaToken =
  /** A Column reference or a Row reference: both read this row's Field. */
  | { kind: "column"; name: string; span: Span }
  | {
      kind: "qualified";
      qualifier: string;
      name: string;
      span: Span;
    }
  /**
   * A Table reference: another Source or Worksheet, by name or (stored) by id,
   * and one of its columns, or the whole table when `column` is null.
   */
  | { kind: "table"; table: TableName; column: string | null; span: Span }
  | { kind: "text"; value: string; span: Span }
  | { kind: "number"; value: string; span: Span }
  | { kind: "name"; value: string; span: Span }
  | { kind: "symbol"; value: string; span: Span }
  | { kind: "invalid"; text: string; reason: InvalidTokenReason; span: Span };

/** How a Table reference names its table: as typed, or by id in stored form. */
export type TableName =
  { kind: "name"; name: string } | { kind: "id"; id: string };

export type InvalidTokenReason =
  "unterminated-text" | "unterminated-column" | "unsupported";

const IDENTIFIER = "[A-Za-z_][A-Za-z0-9_]*";
/** A Column reference; `[@` always starts a Row reference instead. */
const BRACKETED = String.raw`\[(?!@)(?:[^\]]|\]\])*\]`;
/**
 * Excel's this-row structured reference without the table name: `[@Cases]` or
 * `[@[Case Price]]`, where `'` escapes the next character.
 */
const ROW = String.raw`\[@(?:\[(?:'[^]|[^\]'])+\]|(?:'[^]|[^\]\['])+)\]`;
const QUOTED_QUALIFIER = "'(?:[^']|'')*'";
/** A stored Table reference's table: the `{workbook:ID}` Reference grammar. */
const TABLE_ID = String.raw`\{workbook:[^\s}]+\}`;
/**
 * A Table reference's column specifier, as Excel writes one after a table name:
 * `[SKU]`, `[Case Price]` or `[[Case Price]]`, where `'` escapes the next
 * character. `[@…` is a this-row reference, which a Table reference never is.
 */
const COLUMN_SPECIFIER = String.raw`\[(?!@)(?:\[(?:'[^]|[^\]'])+\]|(?:'[^]|[^\]\['])+)\]`;
/**
 * A table named with Excel's quoting for sheet names (`'Supplier Prices'`) or by
 * id may stand alone, as VLOOKUP's table; a bare name needs a column specifier,
 * or it is a function name (see `bareTable`).
 */
const TABLE = `(?:${TABLE_ID}|${QUOTED_QUALIFIER})(?:${COLUMN_SPECIFIER})?|${IDENTIFIER}${COLUMN_SPECIFIER}`;
const matcher = new RegExp(
  [
    String.raw`(?<space>\s+)`,
    String.raw`(?<text>"(?:[^"]|"")*")`,
    `(?<qualified>(?:${QUOTED_QUALIFIER}|${IDENTIFIER})\\.(?:${BRACKETED}|${IDENTIFIER}))`,
    `(?<table>${TABLE})`,
    `(?<row>${ROW})`,
    `(?<column>${BRACKETED})`,
    String.raw`(?<number>\d+(?:\.\d*)?|\.\d+)`,
    // A name not followed by `(` is a table's name, unless it is TRUE or FALSE.
    String.raw`(?<name>${IDENTIFIER})(?:(?<call>(?=\s*\())|)`,
    String.raw`(?<symbol>>=|<=|<>|[+*/&=<>(),-])`,
  ].join("|"),
  "y",
);
const qualifierPrefix = new RegExp(`^(?:${QUOTED_QUALIFIER}|${IDENTIFIER})`);

/** The `=` a Formula may start with is presentation, never part of the expression. */
export function formulaBodyOffset(formula: string): number {
  return /^\s*=/.exec(formula)?.[0].length ?? 0;
}

export function unescapeColumnReference(token: string): string {
  return token.slice(1, -1).replaceAll("]]", "]");
}

/** A bracketed specifier's name: `[x]` or `[[x]]`, with Excel's `'` escapes. */
function unescapeSpecifier(body: string): string {
  const name = body.startsWith("[") ? body.slice(1, -1) : body;
  return name.replaceAll(/'([^])/g, "$1");
}

function unescapeRowReference(token: string): string {
  return unescapeSpecifier(token.slice(2, -1));
}

/**
 * The stored Column reference: `[Name]`, with `]` escaped as `]]`. A name starting
 * with `@` is stored as a Row reference, since `[@…` always reads as one.
 */
export function columnReference(name: string): string {
  if (name.startsWith("@")) return rowReference(name);
  return `[${name.replaceAll("]", "]]")}]`;
}

/** Characters Excel escapes with `'` inside a structured reference. */
const ROW_ESCAPED = /[[\]#'@]/g;
/**
 * Characters for which Excel encloses a column specifier in its own brackets,
 * plus whitespace, which Excel brackets when it writes a reference.
 */
const ROW_BRACKETED = /[\s,:.[\]#'"{}$^&*+=\-<>/\\@!()%?`;~_]/;

/**
 * The Row reference, as Excel writes a this-row structured reference without
 * its table name: `[@Cases]`, `[@[Case Price]]`, `[@['#Items]]`.
 */
export function rowReference(name: string): string {
  const escaped = name.replaceAll(ROW_ESCAPED, "'$&");
  return ROW_BRACKETED.test(name) || !name
    ? `[@[${escaped}]]`
    : `[@${escaped}]`;
}

/** Excel's names that need no quotes: a letter or `_`, then letters, digits and `_`. */
const PLAIN_TABLE_NAME = /^[A-Za-z_][A-Za-z0-9_]*$/;
const BOOLEAN_NAMES = new Set(["TRUE", "FALSE"]);

/**
 * A table's name as a Table reference writes it: bare when it is a plain name,
 * else in quotes with `'` doubled, as Excel quotes a sheet name that has spaces
 * (`'Supplier Prices'`). TRUE and FALSE are quoted so they stay tables.
 */
export function tableNameText(name: string): string {
  return PLAIN_TABLE_NAME.test(name) && !BOOLEAN_NAMES.has(name.toUpperCase())
    ? name
    : `'${name.replaceAll("'", "''")}'`;
}

/** A column specifier as Excel writes one: `[SKU]`, `[Case Price]`, `['#Items]`. */
export function columnSpecifier(column: string): string {
  return `[${column.replaceAll(ROW_ESCAPED, "'$&")}]`;
}

/**
 * The Table reference users read and type: `Prices[SKU]`,
 * `'Supplier Prices'[Case Price]`, or the whole table when `column` is null.
 */
export function tableReference(name: string, column: string | null): string {
  return `${tableNameText(name)}${column === null ? "" : columnSpecifier(column)}`;
}

/** The stored Table reference: the table by id, so renaming it changes nothing. */
export function storedTableReference(
  id: string,
  column: string | null,
): string {
  return `{workbook:${id}}${column === null ? "" : columnSpecifier(column)}`;
}

function tableToken(value: string, span: Span): FormulaToken {
  const id = /^\{workbook:([^\s}]+)\}/.exec(value);
  const prefix = id?.[0] ?? qualifierPrefix.exec(value)?.[0] ?? "";
  const rest = value.slice(prefix.length);
  const table: TableName = id?.[1]
    ? { kind: "id", id: id[1] }
    : {
        kind: "name",
        name: prefix.startsWith("'")
          ? prefix.slice(1, -1).replaceAll("''", "'")
          : prefix,
      };
  return {
    kind: "table",
    table,
    column: rest ? unescapeSpecifier(rest.slice(1, -1)) : null,
    span,
  };
}

function qualifiedToken(value: string, span: Span): FormulaToken {
  const quoted = value.startsWith("'");
  const qualifier = qualifierPrefix.exec(value)?.[0] ?? "";
  const dot = qualifier.length;
  const column = value.slice(dot + 1);

  return {
    kind: "qualified",
    qualifier: quoted
      ? qualifier.slice(1, -1).replaceAll("''", "'")
      : qualifier,
    name: column.startsWith("[") ? unescapeColumnReference(column) : column,
    span,
  };
}

/** A function name or TRUE/FALSE; any other bare name is a whole table. */
function nameToken(
  groups: Record<string, string | undefined>,
  name: string,
  span: Span,
): FormulaToken {
  const value = name.toUpperCase();
  if (groups.call !== undefined || BOOLEAN_NAMES.has(value))
    return { kind: "name", value, span };
  return { kind: "table", table: { kind: "name", name }, column: null, span };
}

/** A qualified, table, row or column reference, if the match is one. */
function referenceToken(
  groups: Record<string, string | undefined>,
  span: Span,
): FormulaToken | undefined {
  if (groups.qualified !== undefined)
    return qualifiedToken(groups.qualified, span);
  if (groups.table !== undefined) return tableToken(groups.table, span);
  if (groups.row !== undefined)
    return { kind: "column", name: unescapeRowReference(groups.row), span };
  if (groups.column !== undefined)
    return {
      kind: "column",
      name: unescapeColumnReference(groups.column),
      span,
    };
  return undefined;
}

function classify(
  groups: Record<string, string | undefined>,
  span: Span,
): FormulaToken | null {
  if (groups.space !== undefined) return null;
  if (groups.text !== undefined)
    return {
      kind: "text",
      value: groups.text.slice(1, -1).replaceAll('""', '"'),
      span,
    };
  const reference = referenceToken(groups, span);
  if (reference) return reference;
  if (groups.number !== undefined)
    return { kind: "number", value: groups.number, span };
  if (groups.name !== undefined) return nameToken(groups, groups.name, span);
  return { kind: "symbol", value: groups.symbol ?? "", span };
}

function invalidToken(formula: string, offset: number): FormulaToken {
  const character = formula[offset] ?? "";
  // An unclosed literal or reference swallows the rest of the text, as an editor
  // reads it; anything after it cannot be a reference.
  const reason: InvalidTokenReason =
    character === '"'
      ? "unterminated-text"
      : character === "["
        ? "unterminated-column"
        : "unsupported";
  const end = reason === "unsupported" ? offset + 1 : formula.length;
  return {
    kind: "invalid",
    text: formula.slice(offset, end),
    reason,
    span: { start: offset, end },
  };
}

/** Tokens of a Formula's body, with spans into the full text (leading `=` included). */
export function lexFormula(formula: string): FormulaToken[] {
  const result: FormulaToken[] = [];
  let offset = formulaBodyOffset(formula);
  while (offset < formula.length) {
    matcher.lastIndex = offset;
    const match = matcher.exec(formula);
    if (!match?.groups) {
      const token = invalidToken(formula, offset);
      result.push(token);
      offset = token.span.end;
      continue;
    }
    const span = { start: offset, end: matcher.lastIndex };
    offset = matcher.lastIndex;
    const token = classify(match.groups, span);
    if (token) result.push(token);
  }
  return result;
}

/** Column references a Formula reads, in order of appearance (duplicates kept). */
export function referencedColumns(formula: string): string[] {
  return lexFormula(formula).flatMap((token) =>
    token.kind === "column" ? [token.name] : [],
  );
}

/** Whether the character at `offset` is inside a text literal, closed or still being typed. */
export function isInsideTextLiteral(formula: string, offset: number): boolean {
  return lexFormula(formula).some(
    (token) =>
      (token.kind === "text" ||
        (token.kind === "invalid" && token.reason === "unterminated-text")) &&
      token.span.start < offset &&
      offset < token.span.end,
  );
}
